/**
 * Unified GraphService facade that combines:
 * - @jkhy/wires-features GraphService (for wires/features)
 * - @jkhy/web-ui-graph/service GraphService (for roles/permissions)
 *
 * This provides a single source of truth for graph-related services
 * and decouples PWA from depending on multiple external packages.
 * When these services consolidate in the future, only this wrapper needs updating.
 */

import { type GraphService as WebUiGraphService } from '@jkhy/web-ui-graph/service';
import { type PortfolioConsumer, type Role } from '@jkhy/web-ui-graph/types/users-and-roles';
import { IInternalUsersService, UserInfo } from './internal-users-client';
export type { UserInfo };
export interface GraphUserRoleLookup {
    roles: Role[];
    users: PortfolioConsumer[];
    matchedUser?: PortfolioConsumer;
    matchedRole?: Role;
}

export interface WireGraphUserEntitlements {
    dailyLimit: number;
    requiredApprovals: number;
    canApproveWires: boolean;
    canCreateOneTimeWires: boolean;
    canCreateWireDrafts: boolean;
}

export interface WireGraphAccountEntitlements {
    canViewAccount: boolean;
    canDepositToAccount: boolean;
    canWithdrawFromAccount: boolean;
    canPerformWiresOnAccount: boolean;
}

export interface WireAccount {
    entitlements: WireGraphAccountEntitlements;
    // accountId: string;
    platformAccount?: unknown;
}

export interface WireUserConfig {
    entitlements: WireGraphUserEntitlements;
    accounts: WireAccount[];
}

/**
 * Wires GraphService interface from @jkhy/wires-features
 */
export interface IWiresGraphService {
    getEnabledFeatures(
        institutionId: string,
        consumerId: string,
        useConsumerApiPrefix?: boolean
    ): Promise<string[]>;
    isWiresEnabled(
        institutionId: string,
        consumerId: string,
        useConsumerApiPrefix?: boolean
    ): Promise<boolean>;
    getConsumerFeatures(
        institutionId: string,
        consumerId: string,
        useConsumerApiPrefix?: boolean
    ): Promise<WireUserConfig>;
}

/**
 * Unified GraphService facade combining wires GraphService and web-ui-graph GraphService.
 * Provides a single DI token that PWA can depend on, making it easier to migrate when services consolidate.
 */
export class GraphService implements IWiresGraphService, IInternalUsersService {
    constructor(
        private wiresGraphService: IWiresGraphService,
        private webUiGraphService: WebUiGraphService,
        private internalUsersService: IInternalUsersService
    ) {}

    // Wires GraphService methods
    public async getEnabledFeatures(
        institutionId: string,
        consumerId: string,
        useConsumerApiPrefix?: boolean
    ): Promise<string[]> {
        return this.wiresGraphService.getEnabledFeatures(
            institutionId,
            consumerId,
            useConsumerApiPrefix
        );
    }

    public async isWiresEnabled(
        institutionId: string,
        consumerId: string,
        useConsumerApiPrefix?: boolean
    ): Promise<boolean> {
        return this.wiresGraphService.isWiresEnabled(
            institutionId,
            consumerId,
            useConsumerApiPrefix
        );
    }

    public async getConsumerFeatures(
        institutionId: string,
        consumerId: string,
        useConsumerApiPrefix?: boolean
    ): Promise<WireUserConfig> {
        return this.wiresGraphService.getConsumerFeatures(
            institutionId,
            consumerId,
            useConsumerApiPrefix
        );
    }

    // Web UI GraphService usersAndRoles methods
    public async getRoles(institutionId: string, portfolioId?: string): Promise<Role[]> {
        return this.webUiGraphService.usersAndRoles.getRoles(institutionId, portfolioId ?? '');
    }

    public async updateUserRole(
        institutionId: string,
        userId: string,
        roleId: string
    ): Promise<boolean> {
        return this.webUiGraphService.usersAndRoles.updateUserRole(institutionId, userId, roleId);
    }

    public async getUsers(
        institutionId: string,
        portfolioId?: string
    ): Promise<PortfolioConsumer[]> {
        return this.webUiGraphService.usersAndRoles.getUsers(institutionId, portfolioId ?? '');
    }

    /**
     * Resolves role information for a provided user id.
     * The caller controls which user is looked up (current authenticated user or another user).
     * This intentionally fetches fresh roles/users per invocation to avoid stale role reads.
     */
    public async getUserRole(
        institutionId: string,
        targetConsumerId: string,
        portfolioId?: string
    ): Promise<GraphUserRoleLookup> {
        const [roles, users] = await Promise.all([
            this.getRoles(institutionId, portfolioId),
            this.getUsers(institutionId, portfolioId),
        ]);

        const matchedUser = users.find(user => user.userId === targetConsumerId);
        const matchedRole = matchedUser
            ? roles.find(role => role.roleId === matchedUser.roleId)
            : undefined;

        return {
            roles,
            users,
            matchedUser,
            matchedRole,
        };
    }

    // IInternalUsersService methods
    public async getInternalUserDetails(institutionId: string, userId: string): Promise<UserInfo> {
        return this.internalUsersService.getInternalUserDetails(institutionId, userId);
    }
}

/**
 * No-op wires graph implementation for applications that only need users/roles graph features.
 */
export class NullWiresGraphService implements IWiresGraphService {
    public async getEnabledFeatures(): Promise<string[]> {
        return [];
    }

    public async isWiresEnabled(): Promise<boolean> {
        return false;
    }

    public async getConsumerFeatures(): Promise<WireUserConfig> {
        return {} as unknown as WireUserConfig;
    }
}

/**
 * No-op graph service used when graph-ui dependencies are intentionally unavailable.
 */
export class NullGraphService implements IWiresGraphService, IInternalUsersService {
    public async getEnabledFeatures(): Promise<string[]> {
        return [];
    }

    public async isWiresEnabled(): Promise<boolean> {
        return false;
    }

    public async getConsumerFeatures(): Promise<WireUserConfig> {
        return {} as unknown as WireUserConfig;
    }

    public async getRoles(): Promise<Role[]> {
        return [];
    }

    public async updateUserRole(): Promise<boolean> {
        return false;
    }

    public async getUsers(): Promise<PortfolioConsumer[]> {
        return [];
    }

    public async getUserRole(): Promise<GraphUserRoleLookup> {
        return {
            roles: [],
            users: [],
            matchedUser: undefined,
            matchedRole: undefined,
        };
    }

    public async getInternalUserDetails(): Promise<UserInfo> {
        return {};
    }
}
